/* import __COLOCATED_TEMPLATE__ from './apply-credit-to-invoice-modal.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { all, dropTask } from 'ember-concurrency';
import { BUTTON_TYPES } from 'sh-www/constants/lsg';
import { SH_INVOICE } from 'sh-www/models/sh-invoice';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showNextModal, closeModal } from 'sh-www/utils/modal-controls';
import { makeMoney } from 'sh-www/helpers/make-money';

const CLASS_NAME = 'SH-c-apply-credit-to-invoice-modal';

const PARTIAL_PAYMENT = 'partial';
const FULL_PAYMENT = 'full';

class BatchedInvoiceBreakdownModal extends Component {
  @service constants;
  @service('commands/sh-invoice-transaction-commands') shInvoiceTransactionCommandService;

  @tracked partialPaymentAmount = null;
  @tracked selectedCostCode = null;
  @tracked selectedRadioOption = null;

  get className() {
    return CLASS_NAME;
  }

  get fullAmount() {
    return FULL_PAYMENT;
  }

  get partialAmount() {
    return PARTIAL_PAYMENT;
  }

  get selectedCostCodeValue() {
    return this.selectedCostCode?.body;
  }

  get maxCreditCanApply() {
    const { unpaidAmount } = this.args.model.shInvoice;
    const { selectedCostCode } = this;

    if (selectedCostCode) {
      const availableCreditBalance = (selectedCostCode.availableCreditInCents / 100).toFixed(2);
      return Math.min(unpaidAmount, Number.parseFloat(availableCreditBalance));
    }

    return unpaidAmount;
  }

  get radioDisabled() {
    if (!this.selectedCostCode) {
      return false;
    }

    // force select radio option
    const { unpaidAmount } = this.args.model.shInvoice;
    const unpaidAmountInCents = unpaidAmount * 100;
    return unpaidAmountInCents > this.selectedCostCode.availableCreditInCents;
  }

  get submitDisabled() {
    return !this.selectedCostCode
      || !this.selectedRadioOption
      || (
        this.selectedRadioOption === PARTIAL_PAYMENT
        && (
          !this.partialPaymentAmount
          || Number.parseFloat(this.partialPaymentAmount) === 0
        )
      );
  }

  @dropTask
  *applyCredit() {
    const {
      currentPage,
      reloadCodeCreditsTask,
      reloadFactoidsTask,
      reloadPageTask,
      shInvoice,
    } = this.args.model;

    const { selectedCostCode } = this;
    const creditCostCodeId = selectedCostCode.id;
    const integrityChecksum = selectedCostCode.integrityChecksum;
    const paymentAmount = this.selectedRadioOption === PARTIAL_PAYMENT
      ? this.partialPaymentAmount
      : shInvoice.unpaidAmount;
    const paymentAmountUSD = makeMoney([paymentAmount]);
    const amount = Number.parseFloat(makeMoney([paymentAmount], { commas: false }).replace('$', ''));

    try {
      // perform apply
      yield this.shInvoiceTransactionCommandService.addTransaction({
        shInvoice,
        amount,
        processorType: SH_INVOICE.PROCESSORS.CREDIT,
        creditCostCodeId,
        integrityChecksum,
      });
    } catch (e) {
      handleErrorResponseWithModal(this, e);
      return;
    }

    try {
      // reload all affected data
      yield all([
        reloadPageTask.perform(currentPage, false),
        reloadFactoidsTask.perform(),
        reloadCodeCreditsTask.perform(),
      ]);

      showNextModal(this, 'confirmation', {
        classNames: `${CLASS_NAME}__success-modal`,
        titleText: 'Credit applied successfully',
        bodyText: `<strong>${paymentAmountUSD}</strong> credit applied to Invoice <strong>#${shInvoice.idWithRevision}</strong>`,
        actionButtonText: 'Ok',
        width: 'medium',
        actionButtonLsgButtonType: BUTTON_TYPES.PRIMARY_REGULAR,
        iconName: 'done',
        isDismissible: false,
      });
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'Credit applied but failed to reload data. Please refresh the page.',
      );
   }
  }

  @action
  handleCostCodeSelected(costCode) {
    this.selectedCostCode = costCode;

    // force select radio option
    if (costCode) {
      const { unpaidAmount } = this.args.model.shInvoice;
      const unpaidAmountInCents = unpaidAmount * 100;

      if (unpaidAmountInCents > costCode.availableCreditInCents) {
        this.selectedRadioOption = PARTIAL_PAYMENT;
        this.partialPaymentAmount = null;
      }
    }
  }

  @action
  handlePartialPaymentAmountInput(amount) {
    const { maxCreditCanApply } = this;

    if (amount > maxCreditCanApply) {
      this.partialPaymentAmount = maxCreditCanApply;
    } else {
      this.partialPaymentAmount = amount;
    }
  }

  @action
  handleRadioOptionSelected(radioOption) {
    this.selectedRadioOption = radioOption;
  }

  @action
  handlePaymentAmountBlur() {
    const { partialPaymentAmount } = this;
    const partialPaymentAmountFloat = Number.parseFloat(partialPaymentAmount);

    // clear bad input
    if (!Number.isFinite(partialPaymentAmountFloat) || partialPaymentAmountFloat <= 0) {
      this.partialPaymentAmount = '';
    } else {
      this.partialPaymentAmount = makeMoney([partialPaymentAmount], { commas: false }).replace('$', '');
    }
  }

  @action
  handleDismiss() {
    closeModal(this);
  }
}

export default BatchedInvoiceBreakdownModal;
